import { useState } from 'react';
import { Button, Modal, Group, Text, Badge } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useUser } from '../../../src/context/UserContext'; // adjust the import path as needed

interface SubscribeButtonProps {
  entityType: 'artist' | 'organizer' | 'area';
  entitySlug: string;
  entityName: string;
  isSubscribed: boolean;
  initialSubscribersCount?: number;
  onSubscribedChange?: (newValue: boolean) => void;
  onSubscribersCountChange?: (count: number) => void;
  fullWidth?: boolean;
}

const SubscribeButton: React.FC<SubscribeButtonProps> = ({
  entityType,
  entitySlug,
  entityName,
  isSubscribed,
  initialSubscribersCount,
  onSubscribedChange,
  onSubscribersCountChange,
  fullWidth,
}) => {
  const { user } = useUser();
  const [openModal, setOpenModal] = useState(false);
  const [subscribed, setSubscribed] = useState(isSubscribed);
  const [subscribersCount, setSubscribersCount] = useState(initialSubscribersCount || 0);

  const endpointBase =
    entityType === 'artist'
      ? `/artists/${entitySlug}`
      : entityType === 'organizer'
        ? `/o/${entitySlug}`
        : `/city/${entitySlug}`;

  const handleSubscribe = async () => {
    if (!user) {
      showNotification({
        color: 'orange',
        title: 'Login Required',
        message: 'You must be logged in to subscribe. Redirecting you to login...',
        icon: <IconX />,
      });

      setTimeout(() => {
        window.location.href = `/login?return_to=${window.location.pathname}`;
      }, 1500);

      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const endpoint = `${endpointBase}/${subscribed ? 'unsubscribe' : 'subscribe'}`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribed(data.is_subscribed);
        setSubscribersCount(data.subscribers_count);
        onSubscribersCountChange?.(data.subscribers_count);
        onSubscribedChange?.(data.is_subscribed);

        showNotification({
          color: 'green',
          title: data.is_subscribed ? 'Subscribed' : 'Unsubscribed',
          message: data.message,
          icon: <IconCheck />,
        });
      } else {
        throw new Error(data.message || 'Failed to process');
      }
    } catch (error: any) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: error.message || 'Something went wrong.',
        icon: <IconX />,
      });
    } finally {
      setOpenModal(false);
    }
  };

  return (
    <>
      <Button
        {...(fullWidth ? { fullWidth: true } : {})}
        variant={subscribed ? 'light' : 'filled'}
        color="orange"
        size="sm"
        onClick={() => setOpenModal(true)}
      >
        <Group gap="xs" justify="center" align="center" wrap="nowrap">
          <Text fw={600}>{subscribed ? 'Subscribed' : 'Subscribe'}</Text>
          {subscribersCount > 0 && (
            <Badge
              color="#EA580C"
              variant="filled"
              size="lg"
              style={{
                fontSize: '0.6rem',
                minHeight: '20px',
                padding: '0 8px',
                height: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '24%',
                lineHeight: 0.8,
              }}
            >
              {subscribersCount}
            </Badge>
          )}
        </Group>
      </Button>

      <Modal
        opened={openModal}
        onClose={() => setOpenModal(false)}
        title={subscribed ? 'Confirm Unsubscribe' : 'Confirm Subscribe'}
        styles={{
          content: {
            backgroundColor: '#171717',
          },
          header: {
            backgroundColor: '#383838',
            borderBottom: 0,
          },
          body: {
            backgroundColor: '#171717',
          },
          title: {
            color: '#fff',
          },
        }}
      >
        <Text className="mt-3">
          Are you sure you want to {subscribed ? 'unsubscribe from' : 'subscribe to'} <strong>{entityName}</strong>?
        </Text>
        <Text size="sm" c="dimmed" className="mb-3">
          {subscribed
            ? `You will no longer receive a weekly email with upcoming events from this ${entityType === 'area' ? 'city' : entityType}.`
            : `You will receive a weekly email every Sunday with upcoming events from this ${entityType === 'area' ? 'city' : entityType}.`}
        </Text>
        <Group justify="flex-end">
          <Button variant="filled" color="gray" onClick={() => setOpenModal(false)}>
            Cancel
          </Button>
          <Button color={subscribed ? 'red' : 'orange'} onClick={handleSubscribe}>
            {subscribed ? 'Unsubscribe' : 'Subscribe'}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default SubscribeButton;
